import React from "react";
import {
  DollarSign,
  Heart,
  Briefcase,
  Shield,
  Activity,
  Home,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

// Composant de carte réutilisable (responsive)
const ServiceCard = ({ icon: Icon, title, description, features, buttonText, gradient }) => (
  <div className="group relative bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden">
    {/* Barre de couleur en haut */}
    <div className={`absolute top-0 left-0 right-0 h-1 ${gradient}`} />

    <div className="relative z-10">
      {/* Icon container */}
      <div
        className={`w-12 h-12 md:w-14 md:h-14 ${gradient} rounded-xl flex items-center justify-center mb-4 md:mb-5 transition-transform duration-300 group-hover:scale-105`}
      >
        <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" strokeWidth={2} />
      </div>

      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">{title}</h3>

      <p className="text-sm text-gray-600 mb-5 md:mb-6 leading-relaxed">{description}</p>

      {/* Liste des caractéristiques */}
      <div className="mb-5 md:mb-6 space-y-2">
        {features.map((feature, index) => (
          <div key={index} className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            <span className="text-xs text-gray-600">{feature}</span>
          </div>
        ))}
      </div>

      <button className="inline-flex items-center gap-2 text-blue-600 font-semibold text-sm group-hover:gap-3 transition-all duration-300">
        {buttonText}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const Services = () => {
  const services = [
    {
      icon: DollarSign,
      title: "Aide financière non remboursable",
      description:
        "Avec AidFoundation, bénéficiez d'une aide financière non remboursable sous forme d'un don financier provenant du budget du FMI, de la banque Mondiale et de la Banque Centrale Européenne pour vos projets de développement.",
      features: [
        "Montants de 2 000€ à 500 000€",
        "Aucun remboursement requis",
        "Financement FMI, Banque Mondiale, BCE",
        "Démarches simplifiées et rapides",
        "Accompagnement personnalisé",
      ],
      buttonText: "En bénéficier",
      gradient: "bg-gradient-to-br from-blue-500 to-blue-700",
    },
    {
      icon: Shield,
      title: "Assurance vie gratuite",
      description:
        "Pour vous permettre de garder votre sérénité, AidFoundation vous propose une assurance vie adaptée à vos besoins. À chaque projet son financement, à chaque financement son assurance, vous voici bien protégé. Une action rendue possible grâce à une collaboration entre le FMI, la Banque Mondiale et la Banque Centrale Européenne.",
      features: [
        "Épargne sécurisée avec rendements attractifs",
        "Avantages fiscaux optimisés",
        "Transmission de patrimoine facilitée",
        "Options de versements flexibles",
        "Garanties décès et invalidité",
      ],
      buttonText: "En bénéficier",
      gradient: "bg-gradient-to-br from-indigo-500 to-purple-600",
    },
    {
      icon: Briefcase,
      title: "Bourse familiale",
      description:
        "La bourse familiale est un financement non remboursable que l'Union Européenne en Collaboration avec la Banque Mondiale a lancé pour lutter contre la pauvreté dans le monde. Veuillez vous inscrire sur la liste des bénéficiaires auprès de AidFoundation afin d'en bénéficier.",
      features: [
        "Financement pour les familles",
        "Sans conditions de remboursement",
        "Programme Union Européenne",
        "Aide contre la pauvreté",
        "Inscription simple et rapide",
      ],
      buttonText: "En bénéficier",
      gradient: "bg-gradient-to-br from-green-500 to-emerald-600",
    },
  ];

  const assurances = [
    {
      icon: Heart,
      title: "Aide aux Personnes Âgées",
      description:
        "Soutien financier dédié à la prise en charge de la dépendance des seniors. Un accompagnement sur-mesure pour améliorer le quotidien des personnes âgées à faibles ressources et garantir leur dignité.",
      features: [
        "Aide pour la dépendance et les soins",
        "Financement des services à domicile",
        "Adaptation du logement",
        "Prise en charge des frais médicaux",
        "Conseils et suivi personnalisé",
      ],
      buttonText: "En savoir plus",
      gradient: "bg-gradient-to-br from-purple-500 to-pink-600",
    },
    {
      icon: Activity,
      title: "Complémentaire Santé",
      description:
        "Une couverture santé complète pour vous et votre famille. Remboursements optimisés, accès privilégié à un large réseau de professionnels de santé et garanties adaptées à tous les besoins médicaux.",
      features: [
        "Remboursement jusqu'à 300%",
        "Réseau de + 50 000 professionnels",
        "Téléconsultation 24/7 incluse",
        "Couverture optique et dentaire",
        "Tarifs famille avantageux",
      ],
      buttonText: "En savoir plus",
      gradient: "bg-gradient-to-br from-red-500 to-orange-500",
    },
    {
      icon: Home,
      title: "Assurance Emprunteur",
      description:
        "Protégez vos proches en cas d'imprévus. Une assurance emprunteur compétitive et flexible pour sécuriser vos projets immobiliers ou professionnels avec des garanties complètes et des tarifs négociés.",
      features: [
        "Couverture décès et invalidité",
        "Protection perte d'emploi",
        "Tarifs jusqu'à 50% moins chers",
        "Garanties adaptées",
        "Délégation simplifiée",
      ],
      buttonText: "En savoir plus",
      gradient: "bg-gradient-to-br from-teal-500 to-cyan-600",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-16 sm:pt-20 md:pt-24 pb-16 md:pb-20">
      {/* En-tête */}
      <div className="text-center mb-10 md:mb-16 px-4">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
          Nos Services & Assurances
        </h1>
        <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
          Découvrez nos solutions de financement adaptées à vos besoins
        </p>
      </div>

      {/* Section Services */}
      <section className="container mx-auto px-4 mb-14 md:mb-20 max-w-7xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {services.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </div>
      </section>

      {/* Section Assurances */}
      <section className="container mx-auto px-4 max-w-7xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {assurances.map((assurance, index) => (
            <ServiceCard key={index} {...assurance} />
          ))}
        </div>
      </section>

      {/* ✅ CTA (plus clean + espace en haut) */}
      <section className="container mx-auto px-4 max-w-6xl mt-10 md:mt-14">
        <div className="relative bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
          {/* Accent bar (sobre, premium) */}
          <div className="h-1.5 bg-gradient-to-r from-blue-600 to-indigo-600" />

          {/* Décor léger */}
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-60" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-indigo-100 rounded-full blur-3xl opacity-60" />

          <div className="relative z-10 p-7 sm:p-10 md:p-12 text-center">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Prêt à concrétiser votre projet ?
            </h3>

            <p className="text-gray-600 text-base sm:text-lg mb-7 max-w-2xl mx-auto">
              Notre équipe est à votre disposition pour vous accompagner dans le choix de la solution la plus adaptée à vos besoins.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
              <button className="inline-flex justify-center items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 sm:px-8 py-3 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg">
                Nous contacter
                <ArrowRight className="w-5 h-5" />
              </button>

              <button className="inline-flex justify-center items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 font-semibold px-6 sm:px-8 py-3 rounded-lg transition-all duration-300 border border-gray-300">
                Faire une demande
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
